import store from '@/store'
import api from '@/api'
import {
  Room, ImageFile, Portfolio,
} from './types';

enum SignupType {
  COLLABORATION = 'collaboration',
  CONFIDENCE = 'confidence',
  REFRESH = 'refresh',
  REMODEL = 'remodel',
}

const signupTypes: { [key: string]: string } = {
  [SignupType.COLLABORATION]: 'Collaborative E-design',
  [SignupType.CONFIDENCE]: 'Style Confidence',
  [SignupType.REFRESH]: 'Room Refresh',
  [SignupType.REMODEL]: 'Remodel Consulting',
}

function hasRoom(room: Room) {
  return Object.values(room).filter(i => i == null).length < Object.values(room).length
}

export default {
  async deleteRoomImage(signupId: string, image: ImageFile) {
    try {
      await api.deleteSignupImage(signupId, image.remoteFilename)
      store.setRoomFiles(
        store.state.roomFiles.filter(f => f.remoteFilename !== image.remoteFilename),
      )
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async deleteInspirationImage(signupId: string, image: ImageFile) {
    try {
      await api.deleteSignupImage(signupId, image.remoteFilename)
      store.setInspirationFiles(
        store.state.inspirationFiles.filter(f => f.remoteFilename !== image.remoteFilename),
      )
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async deleteDimensionsImage(signupId: string, image: ImageFile) {
    try {
      await api.deleteSignupImage(signupId, image.remoteFilename)
      store.setDimensionsFiles(
        store.state.dimensionsFiles.filter(f => f.remoteFilename !== image.remoteFilename),
      )
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async fetchSignupImages(files: Array<ImageFile>) {
    if (files == null) return {}

    // build map of { filename: BLOB, ... }
    return Promise.all(
      files
        .map(({ remoteFilename }) => api.getSignupImage(store.state.signupId, remoteFilename)),
    ).then(bins => bins.reduce((acc, cur, idx) => {
      acc[files[idx].filename] = URL.createObjectURL(cur)
      return acc
    }, {} as { [key: string]: string }))
  },

  async fetchSignupAdminImages(signupId: string, files: Array<ImageFile>) {
    if (files == null) return {}

    // build map of { filename: BLOB, ... }
    return Promise.all(
      files
        .map(({ remoteFilename }) => api.getSignupAdminImage(signupId, remoteFilename)),
    ).then(bins => bins.reduce((acc, cur, idx) => {
      acc[files[idx].filename] = URL.createObjectURL(cur)
      return acc
    }, {} as { [key: string]: string }))
  },

  async getRandomPortfolioPhoto() {
    const portfolio = await api.getRandomPortfolio()
    const photos = (portfolio && portfolio.images) || []
    const randomIndex = Math.floor(Math.random() * Math.floor(photos.length))
    return { id: portfolio.id, name: portfolio.name, image: photos[randomIndex] }
  },

  async getSignup(code: string) {
    try {
      store.clearSignup()
      const {
        id, type, customer, images = [], room,
      } = await api.getSignupByCode(code)
      const dimensionsFiles = images.filter((i: ImageFile) => i.type === 'dimensions')
      const inspirationFiles = images.filter((i: ImageFile) => i.type === 'inspiration')
      const roomFiles = images.filter((i: ImageFile) => i.type === 'room')
      store.setCustomer(customer)
      store.setRoom(room)
      store.setSignupId(id)
      store.setSignupCode(code)
      store.setSignupType(type, signupTypes[type])
      store.setDimensionsFiles(dimensionsFiles)
      store.setInspirationFiles(inspirationFiles)
      store.setRoomFiles(roomFiles)
      store.setCustomerIsPersisted(true)
      if (hasRoom(room)) store.setRoomIsPersisted(true)
      if (dimensionsFiles.length > 0) store.setDimensionsFilesArePersisted(true)
      if (inspirationFiles.length > 0) store.setInspirationFilesArePersisted(true)
      if (roomFiles.length > 0) store.setRoomFilesArePersisted(true)
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async saveCustomer() {
    if (store.state.signupId) {
      try {
        await api.putCustomer(store.state.signupId, store.state.customer)
        store.setCustomerIsPersisted(true)
        return
      } catch (err) {
        store.setErrorMessage(err.message)
      }
    }

    try {
      const { id, code } = await api.createSignup({
        customer: store.state.customer,
        type: store.state.signupType.key,
      })
      store.setSignupId(id)
      store.setSignupCode(code)
      store.setCustomerIsPersisted(true)
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async saveDimensionsFiles(body: FormData) {
    try {
      const { images } = await api.putDimensionsFiles(store.state.signupId, body)
      store.setDimensionsFiles(images.find((i: ImageFile) => i.type === 'dimensions'))
      store.setDimensionsFilesArePersisted(true)
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async saveInspirationFiles(body: FormData) {
    try {
      const { images } = await api.putInspirationFiles(store.state.signupId, body)
      store.setInspirationFiles(images.filter((i: ImageFile) => i.type === 'inspiration'))
      store.setInspirationFilesArePersisted(true)
    } catch (err) {
      store.setErrorMessage(err.message)
      // TODO rethrow to prevent component route change?
    }
  },

  async saveRoom() {
    try {
      await api.putRoom(store.state.signupId, store.state.room)
      store.setRoomIsPersisted(true)
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async saveRoomFiles(body: FormData) {
    try {
      const { images } = await api.putRoomFiles(store.state.signupId, body)
      store.setRoomFiles(images.filter((i: ImageFile) => i.type === 'room'))
      store.setRoomFilesArePersisted(true)
    } catch (err) {
      store.setErrorMessage(err.message)
      // TODO rethrow to prevent component route change?
    }
  },

  async createPortfolio(body: Portfolio, photos: FormData) {
    try {
      const portfolio = await api.createPortfolio(body)
      await api.putPortfolioFiles(portfolio.id, photos)
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

  async updatePortfolio(id: string, body: Portfolio, photos: FormData) {
    try {
      photos.set('portfolio', JSON.stringify(body))
      await api.updatePortfolio(id, photos)
    } catch (err) {
      store.setErrorMessage(err.message)
    }
  },

}
